import React, { useState } from 'react';
import { ArrowRight, ExternalLink, ImageOff } from 'lucide-react';
import { Link } from 'react-router-dom';
import { useIntersectionObserver } from '../hooks/useIntersectionObserver';
import { useSpotlight } from '../hooks/useSpotlight';
import { useI18n } from '../i18n/I18nContext';

interface PortfolioCardProps {
  title: string;
  category: string;
  description: string;
  image: string;
  caseStudyId?: string;
  link?: string;
  animationDirection?: 'left' | 'right';
  projectType: 'showcase' | 'real-world';
}

function PortfolioCard({ title, category, description, image, caseStudyId, link, projectType, animationDirection = 'left' }: PortfolioCardProps) {
  const { t, localizedPath } = useI18n();
  const [isVisible, setIsVisible] = useState(false);
  const [imgError, setImgError] = useState(false);

  const ref = useIntersectionObserver({
    onIntersect: () => setIsVisible(true),
    threshold: 0.05,
    rootMargin: '0px 0px -20px 0px',
  });

  const hasExternalLink = Boolean(link && link.trim().length > 0);
  const handleSpotlight = useSpotlight<HTMLDivElement>();

  const imageContent = imgError ? (
    <div className="w-full h-full flex flex-col items-center justify-center gap-3 text-gray-500">
      <ImageOff className="h-10 w-10" />
      <span className="text-sm font-medium text-center px-4">{title}</span>
    </div>
  ) : (
    <img
      src={image}
      alt={title}
      onError={() => setImgError(true)}
      className="w-full h-full object-contain p-4 transform group-hover:scale-105 transition-all duration-700 ease-in-out"
    />
  );

  return (
    <div
      ref={ref as React.RefObject<HTMLDivElement>}
      onPointerMove={handleSpotlight}
      className={`portfolio-card spotlight-card bg-zinc-900 rounded-lg overflow-hidden border border-zinc-800 group hover:border-[#FDCB12] transform hover:-translate-y-2 transition-all duration-700 ease-out ${
        isVisible ? 'opacity-100 translate-x-0 translate-y-0' : `opacity-0 translate-y-6 ${animationDirection === 'left' ? '-translate-x-2' : 'translate-x-2'}`
      }`}
    >
      <div className="portfolio-card__media relative h-48 overflow-hidden bg-white">
        {hasExternalLink ? (
          <a
            href={link}
            target="_blank"
            rel="noopener noreferrer"
            aria-label={`${t.portfolio.visitProjectWebsite} - ${title}`}
            className="block h-full"
          >
            {imageContent}
          </a>
        ) : (
          imageContent
        )}
        <div className="absolute inset-0 pointer-events-none bg-gradient-to-t from-black/80 to-transparent" />
        <span className="absolute bottom-4 left-4 text-sm bg-[#FDCB12] text-black px-3 py-1 rounded-full">
          {category}
        </span>
      </div>
      <div className="p-6 flex flex-col h-full">
        <div className="mb-3 flex flex-wrap items-center gap-2">
          <h3 className="text-xl font-bold text-white">{title}</h3>
          <span
            className={`text-xs font-semibold uppercase tracking-wide px-2.5 py-1 rounded-full ${
              projectType === 'real-world'
                ? 'bg-emerald-500/20 text-emerald-300 border border-emerald-400/40'
                : 'bg-blue-500/20 text-blue-300 border border-blue-400/40'
            }`}
          >
            {projectType === 'real-world' ? t.portfolio.production : t.portfolio.showcase}
          </span>
        </div>
        <p className="text-gray-400 mb-4">{description}</p>
        {hasExternalLink && (
          <a
            href={link}
            target="_blank"
            rel="noopener noreferrer"
            className="mb-4 inline-flex items-center gap-2 text-sm font-medium text-[#FDCB12] hover:text-[#FF5500] transition-colors duration-300"
            aria-label={`${t.portfolio.visitSite} - ${title}`}
          >
            {t.portfolio.visitSite} <ArrowRight className="h-4 w-4" />
          </a>
        )}
        <div className="flex-1" />
        <div className="flex items-center gap-4 pt-4 border-t border-zinc-700">
          {caseStudyId && (
            <Link
              to={localizedPath(`/case-study/${caseStudyId}`)}
              className="text-[#FDCB12] font-medium flex items-center hover:text-[#FF5500] transition-all duration-300 group"
            >
              {t.portfolio.viewCaseStudy} <ArrowRight className="ml-1 h-4 w-4 group-hover:translate-x-1 transition-transform" />
            </Link>
          )}
          {hasExternalLink && (
            <a
              href={link}
              target="_blank"
              rel="noopener noreferrer"
              className="inline-flex items-center rounded-md bg-[#FDCB12] px-3 py-2 text-sm font-semibold text-black hover:bg-[#FF5500] hover:text-white transition-all duration-300"
              aria-label={`${t.portfolio.openProject} - ${title}`}
            >
              {t.portfolio.openProject} <ExternalLink className="ml-2 h-4 w-4" />
            </a>
          )}
        </div>
      </div>
    </div>
  );
}

export default PortfolioCard;
